import React, { useState } from 'react';
import ScrollerNav from './ScrollerNav';
import NavSection from './NavSection';
import { NavItem } from './types/NavItem';

/**
 * A hook for rendering a navigation bar that scrolls to specific sections of the page.
 * @param navTitle Title to show on the nav bar
 * @param navItems Sections of the page we want to display
 * @param navBarCtaInfo info for the call-to-action button on the nav bar. If omitted, don't render it.
 * @returns A tuple of functions to render the nav bar & render corresponding sections based off of the navItem id.
 */
const useScrollerNav = (
  navTitle: string,
  navItems: NavItem[],
  navBarCtaInfo?: { title: string; onClick: () => void }
): { renderNavBar: () => JSX.Element; renderSection: (id: string, children: React.ReactNode) => JSX.Element; selectedNavId: string } => {
  const [selectedNavId, setSelectedNavId] = useState(navItems.length ? navItems[0].id : '');

  const renderNavBar = () => (
    <ScrollerNav items={navItems} title={navTitle} selectedTab={selectedNavId} onTabChange={setSelectedNavId} ctaInfo={navBarCtaInfo} />
  );

  const renderSection = (id: string, children: React.ReactNode) => (
    <NavSection id={id} setAsSelectedSection={() => setSelectedNavId(id)}>
      {children}
    </NavSection>
  );

  return { renderNavBar, renderSection, selectedNavId };
};

export default useScrollerNav;
